import { HttpClient } from '@angular/common/http';
import { FormBuilder } from '@angular/forms';
import { Column } from '@import/classes';
import { InputType } from '@import/enums/input-type.enum';
import { StepChange } from '@import/enums/step-change.enum';
import { ValidationState } from '@import/enums/validation-state.enum';
import { getErrorRows, getRowErrorCount } from '@import/functions/rows';
import { serializePayload, serializeRow } from '@import/functions/serialize.function';
import { ColumnType } from '@import/interfaces';
import { ProcessedRow } from '@import/interfaces/processed-row.interface';
import { ErrorMap, ValidationError, ValidationResponse } from '@import/interfaces/validation.interface';
import { EmployeeHumiColumnService } from '@import/services/column-type/employee-humi-column.service';
import { isEmpty } from 'lodash-es';

export abstract class RowAndColumnService {
    columns: Column[] = [];
    rows: string[][] = [];
    processedRows: ProcessedRow[] = [];
    primaryColumn: ColumnType;

    protected lastChange: StepChange;
    protected abstract readonly type: string;
    protected abstract validateUrl: string;
    protected abstract importUrl: string;
    protected knownRowHelperValues: string[] = [];
    protected knownColumnHelperValue = '';

    constructor(
        protected http: HttpClient,
        protected formBuilder: FormBuilder,
        protected employeeHumiColumnService: EmployeeHumiColumnService
    ) {}

    setLastChange(change: StepChange): void {
        this.lastChange = change;
    }

    removeProcessedRow(row: ProcessedRow): void {
        const removeIndex = this.processedRows.findIndex((r: ProcessedRow) => r === row);
        this.processedRows = [
            ...this.processedRows.slice(0, removeIndex),
            ...this.processedRows.slice(removeIndex + 1),
        ];
    }

    getRowPrimaryValue(row: ProcessedRow): null | string | number {
        if (!this.primaryColumn) {
            return null;
        }

        return this.processedRows.find((r) => r === row).raw[this.primaryColumn.csvColumn.index];
    }

    setPrimaryColumnByType(humiColumn: ColumnType): void {
        // Set the primary column as the first column
        this.primaryColumn = humiColumn;
    }

    setColumnsAndRows(columns: Column[], rows: string[][]): void {
        this.columns = columns;
        this.rows = this.filterEmptyRows(rows);

        if (this.hasFileHaveHelperRow(this.rows)) {
            this.rows = this.stripHelperRows(this.rows);
        }

        if (this.hasFileHaveHelperColumn(this.columns)) {
            this.columns = this.stripHelperColumns(this.columns);
            this.rows = this.removeStartingEmptyRowColumns(this.rows);
            this.columns = this.reindexColumns(this.columns);
        }

        this.columns.forEach((column: Column) => {
            column.setValues(this.rows);
        });
    }

    reindexColumns(columns: Column[]): Column[] {
        return columns.map((column, index) => {
            column.index = index;
            return column;
        });
    }

    validateRow(row: ProcessedRow, meta: {} = {}): Promise<void> {
        row.isRequesting = true;
        return this.http
            .post<ValidationResponse>(this.validateUrl, {
                data: serializeRow(this.type, this.employeeHumiColumnService.mappedColumns, row.raw),
                meta: meta,
            })
            .toPromise<ValidationResponse>()
            .then((response: ValidationResponse) => {
                row.validationResponse = response;

                row.errors = row.validationResponse.data.errors.reduce(
                    (errorMapping: ErrorMap, error: ValidationError) => {
                        const columnWithError = this.employeeHumiColumnService.mappedColumns.find(
                            (humiColumn: ColumnType) => humiColumn.serializeName.includes(error.source.pointer)
                        );

                        if (columnWithError) {
                            errorMapping[columnWithError.csvColumn.index] = error.detail;
                        }

                        return errorMapping;
                    },
                    {}
                );

                row.isRequesting = false;
            })
            .catch(() => {
                row.isRequesting = false;
            });
    }

    getRowCountByValidationState(state: ValidationState): number {
        return this.processedRows.filter((row: ProcessedRow) => row.states === state).length;
    }

    getErrorCount(): number {
        return getErrorRows(this.processedRows).reduce((count: number, row: ProcessedRow) => {
            count += getRowErrorCount(row);

            return count;
        }, 0);
    }

    importPayload(): Promise<any> {
        return this.http
            .post<ValidationResponse>(this.importUrl, {
                data: serializePayload(
                    this.type,
                    this.employeeHumiColumnService.getMappedHumiColumns(),
                    this.processedRows
                ),
            })
            .toPromise();
    }

    processRows(): void {
        const rows = this.applyDataTransformations(this.rows, this.employeeHumiColumnService.getMappedHumiColumns());
        this.processedRows = this.createFormControls(rows).sort(this.sortFunction.bind(this));
        this.setLastChange(StepChange.ProcessedRows);
    }

    createFormControls(rows: ProcessedRow[]): ProcessedRow[] {
        rows.forEach((r: ProcessedRow) => {
            r.formControls = this.formBuilder.group(
                r.raw.reduce((accumulator: { [key: number]: string[] }, val: string, index: number) => {
                    accumulator[index] = [val];

                    return accumulator;
                }, {})
            );
        });

        return rows;
    }

    isTypeAssigned(type: ColumnType): boolean {
        return !!this.columns.find((c: Column) => c.type === type);
    }

    shouldSmartMatchValues(): boolean {
        return this.lastChange < StepChange.ValueMapping;
    }

    shouldSmartMatchColumns(): boolean {
        return (
            this.lastChange < StepChange.ColumnMapping || isEmpty(this.employeeHumiColumnService.getMappedHumiColumns())
        );
    }

    shouldProcessRows(): boolean {
        return this.processedRows.length === 0;
    }

    protected hasFileHaveHelperRow(rows: string[][]): boolean {
        if (!rows.length) {
            return false;
        }
        return rows[0].some((element) => this.knownRowHelperValues.includes(element));
    }

    protected hasFileHaveHelperColumn(columns: Column[]): boolean {
        if (!columns.length) {
            return false;
        }
        return columns[0].name === this.knownColumnHelperValue;
    }

    protected stripHelperColumns(columns: Column[]): Column[] {
        return columns.filter((column) => column.name !== this.knownColumnHelperValue);
    }

    protected stripHelperRows(rows: string[][]): string[][] {
        const cleansedRows = [...rows];
        cleansedRows.shift();
        return cleansedRows;
    }

    /*
     * Strip the first column from the rest of the rows if the helper column was present
     * otherwise the row columns wouldn't correspond correctly with the headers.
     */
    protected removeStartingEmptyRowColumns(rows: string[][]): string[][] {
        const cleansedRows = [...rows];
        cleansedRows.forEach((row) => row.splice(0, 1));
        return cleansedRows;
    }

    protected abstract sortFunction(a: ProcessedRow, b: ProcessedRow): number;

    protected applyDataTransformations(rows: string[][], humiColumns: ColumnType[]): ProcessedRow[] {
        const processedRows = rows.map((row: string[]) => row.map((val: string) => val));

        humiColumns
            .filter((c: ColumnType) => c.csvColumn.valueMapping.size)
            .forEach((column: ColumnType) => {
                processedRows.forEach((row: string[]) => {
                    const rowData = row[column.csvColumn.index];
                    const sanitizeData = this.sanitizeData(column.csvColumn, rowData);
                    const mappedValue = column.csvColumn.valueMapping.get(rowData);
                    row[column.csvColumn.index] = mappedValue !== undefined ? mappedValue : sanitizeData;
                });
            });

        humiColumns
            .filter((c: ColumnType) => !c.csvColumn.valueMapping.size)
            .forEach((column: ColumnType) => {
                processedRows.forEach((row: string[]) => {
                    const rowData = row[column.csvColumn.index];
                    row[column.csvColumn.index] = rowData ? this.sanitizeData(column.csvColumn, rowData) : rowData;
                });
            });

        return processedRows.map((row: string[], id: number): ProcessedRow => {
            return {
                id,
                raw: row,
                states: null,
                errors: {},
                isRequesting: false,
                validationResponse: { data: null },
                formControls: null,
            };
        });
    }

    protected sanitizeData(column: Column, value: any): any {
        if (column.type && column.type.input === InputType.integer) {
            return parseInt(value.replace(/[^0-9.]+/g, ''), 10);
        }
        return value;
    }

    private filterEmptyRows(rows: string[][]): string[][] {
        return rows.filter((r: string[]) => {
            return (
                r.length > 0 &&
                !r.every((val: string) => val === null || val === undefined || String(val).trim() === '')
            );
        });
    }
}
